import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { PrintLetterHead, useLetterHead } from '../../components/ui/PrintLetterHead';
import { useIpdBill, useIpdDaycareAdmission } from './ipd-daycare.queries';
import { currency, formatIpdDateTime } from './ipd-daycare.utils';

const upper = (value: string | number | null | undefined) => String(value ?? '-').toUpperCase();

export const IpdBillPrintPage = () => {
  const { admissionId, billId } = useParams();
  const [params] = useSearchParams();
  const withHeader = params.get('header') !== '0';
  const admissionQuery = useIpdDaycareAdmission(admissionId);
  const billQuery = useIpdBill(billId);
  const letterHead = useLetterHead(withHeader);

  useEffect(() => {
    if (admissionQuery.data && billQuery.data && letterHead.isReady) window.setTimeout(() => window.print(), 300);
  }, [admissionQuery.data, billQuery.data, letterHead.isReady]);

  if (admissionQuery.isLoading || billQuery.isLoading) return <LoadingState />;
  if (admissionQuery.isError || !admissionQuery.data) return <ErrorState title="Admission not found" message="The admission could not be loaded." />;
  if (billQuery.isError || !billQuery.data) return <ErrorState title="Bill not found" message="The bill could not be loaded." />;

  const admission = admissionQuery.data;
  const bill = billQuery.data;
  const patient = admission.patientSnapshot;
  const age = `${patient.ageYears}Y ${patient.ageMonths}M ${patient.ageDays}D`;
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', ') || '-';
  const activeCharges = bill.charges.filter((line) => !line.cancelled);

  return (
    <div className="print-bill-page">
      {withHeader ? <PrintLetterHead imageUrl={letterHead.imageUrl} /> : null}

      <table className="print-bill-header">
        <tbody>
          <tr>
            <td colSpan={2} className="print-bill-title">Bill Details ( {admission.admissionType} Patient )</td>
          </tr>
          <tr>
            <td>
              <p className="print-bill-patient-name">PATIENT NAME : {upper(patient.name)}</p>
              <p><b>UHID: </b>{upper(patient.uhid)}</p>
              <p><b>AGE :</b> {upper(age)} &nbsp;&nbsp;&nbsp;&nbsp; <b>SEX :</b> {upper(patient.gender)}</p>
              <p><b>ADDRESS :</b> {upper(address)}</p>
              <p><b>MOBILE NO:</b> {upper(patient.phone)}</p>
            </td>
            <td>
              <p><b>BILL NO. &amp; DATE: </b>{upper(bill.billNumber)} <b>&amp;</b> {upper(formatIpdDateTime(bill.billDate))}</p>
              <p><b>ADMISSION NO:</b> {upper(admission.ipdNumber)}</p>
              <p><b>UNDER DOCT:</b> {upper(admission.underDoctor ? `Dr. ${admission.underDoctor.name}` : '-')}</p>
              <p><b>ADMN. DATE &amp; TIME:</b> {upper(formatIpdDateTime(admission.admissionDate))}</p>
              <p><b>BED DETAILS :</b> {upper(`${admission.bedCategory.name} - ${admission.bed.name}`)}</p>
              <p><b>PATIENT TYPE : </b>{upper(admission.insuranceType)}</p>
              <p><b>PACKAGE TYPE : </b>{upper(admission.packageType)}</p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className="print-bill-charges">
        <thead>
          <tr>
            <th className="print-bill-col-index">#</th>
            <th>Date</th>
            <th>Particulars</th>
            <th className="print-bill-col-num">Qty</th>
            <th className="print-bill-col-num">Rate</th>
            <th className="print-bill-col-num">Dis.</th>
            <th className="print-bill-col-num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {activeCharges.map((line, index) => (
            <tr key={line.id}>
              <td>{index + 1}</td>
              <td>{formatIpdDateTime(line.date)}</td>
              <td>{line.chargeName}{line.doctor ? <><br /><small>(Dr. {line.doctor.name})</small></> : null}</td>
              <td className="print-bill-col-num">{line.quantity}</td>
              <td className="print-bill-col-num">{line.rate}</td>
              <td className="print-bill-col-num">{line.discountAmount}</td>
              <td className="print-bill-col-num">{line.amount}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={6} className="print-bill-col-num">Sub Total</th>
            <th className="print-bill-col-num">{bill.subTotal}</th>
          </tr>
        </tfoot>
      </table>

      <table className="print-bill-totals">
        <tbody>
          <tr>
            <td>TOTAL :</td>
            <td className="print-bill-col-num">{currency(bill.total)}</td>
          </tr>
          <tr>
            <td>NET AMOUNT :</td>
            <td className="print-bill-col-num">{currency(bill.total)}</td>
          </tr>
          <tr>
            <td>DISCOUNT :</td>
            <td className="print-bill-col-num">{currency(bill.totalDiscount)}</td>
          </tr>
          <tr>
            <td>TOTAL PAYABLE AMOUNT :</td>
            <td className="print-bill-col-num">{currency(bill.grandTotal)}</td>
          </tr>
          <tr>
            <td>TOTAL PAID AMOUNT :</td>
            <td className="print-bill-col-num">{currency(bill.totalPayment)}</td>
          </tr>
          {bill.dueAmount > 0 ? (
            <tr className="print-bill-due-row">
              <td>DUE AMOUNT :</td>
              <td className="print-bill-col-num">{currency(bill.dueAmount)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <div className="print-bill-signoff">
        <span>PREPARED BY: {bill.createdBy.name}</span>
        <span>Date: {formatIpdDateTime(new Date().toISOString())}</span>
      </div>
    </div>
  );
};
